/**
 * DataTable — molecule (CES standard)
 * TypeScript replica of the client portal's canonical molecules/DataTable:
 * column definitions with optional custom render, gray header, divided
 * rows, empty state. Adds an optional per-row test id for E2E.
 */

import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  key: string;
  header: string;
  className?: string;
  render?: (row: Row) => ReactNode;
}

export function DataTable<Row>({
  data,
  columns,
  emptyMessage = 'No data available',
  rowTestId,
  className = '',
}: {
  data: Row[];
  columns: DataTableColumn<Row>[];
  emptyMessage?: string;
  rowTestId?: (row: Row) => string;
  className?: string;
}) {
  if (data.length === 0) {
    return (
      <div className="py-8 text-center">
        <h3 className="text-sm font-medium text-gray-900">{emptyMessage}</h3>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-lg border border-gray-200 shadow-sm ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full bg-white text-xs">
          <thead className="bg-gray-200 border-b border-gray-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-700 ${
                    column.className ?? ''
                  }`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((row, rowIndex) => (
              <tr
                key={rowTestId ? rowTestId(row) : rowIndex}
                className="hover:bg-gray-50 transition-colors duration-150"
                data-testid={rowTestId ? rowTestId(row) : undefined}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`whitespace-nowrap px-3 py-2 text-xs text-gray-900 ${
                      column.className ?? ''
                    }`}
                  >
                    {column.render
                      ? column.render(row)
                      : (((row as Record<string, unknown>)[column.key] as ReactNode) ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
